/**
 * Cross-origin-isolation bootstrap for Zoom call tabs (ENG-3306).
 *
 * The Zoom call routes are served with COOP/COEP so the Meeting SDK can use
 * SharedArrayBuffer (native View menu). A SPA soft-navigation into the route
 * loads a document without those headers, so the page must fully reload once
 * before starting any Zoom or network work. sessionStorage marks the retry so
 * a misconfigured server cannot cause a reload loop.
 */

export const ZOOM_COI_RELOAD_KEY = 'unify-zoom-coi-reload';

export type ZoomCoiAction =
  | { kind: 'proceed' }
  | { kind: 'reload' }
  | { kind: 'fail' };

/**
 * Pure decision: given the current isolation state, choose the bootstrap action.
 *
 * - isolated → proceed (and callers should clear the retry marker)
 * - not isolated, not yet retried → reload once
 * - not isolated, already retried → fail (no Zoom init, no loop)
 */
export function decideZoomCoiAction(input: {
  crossOriginIsolated: boolean;
  alreadyRetried: boolean;
}): ZoomCoiAction {
  if (input.crossOriginIsolated) {
    return { kind: 'proceed' };
  }
  if (input.alreadyRetried) {
    return { kind: 'fail' };
  }
  return { kind: 'reload' };
}

export function readZoomCoiRetryMarker(storage: Pick<Storage, 'getItem'>): boolean {
  try {
    return storage.getItem(ZOOM_COI_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

/** Returns false when the marker could not be persisted (fail closed, never loop). */
export function writeZoomCoiRetryMarker(storage: Pick<Storage, 'setItem'>): boolean {
  try {
    storage.setItem(ZOOM_COI_RELOAD_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

export function clearZoomCoiRetryMarker(storage: Pick<Storage, 'removeItem'>): void {
  try {
    storage.removeItem(ZOOM_COI_RELOAD_KEY);
  } catch {
    // ignore quota / private-mode failures
  }
}
